import { createStyles, getRootProps } from '@udecode/plate-styled-components';
import _styled, { css } from 'styled-components';
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { findNodePath, setNodes, usePlateEditorRef } from '@udecode/plate-core';
import { useSelected, useReadOnly } from 'slate-react';
import videoParser from 'js-video-url-parser';
import { insertMediaEmbed } from '@udecode/plate-media-embed';
import { ToolbarButton } from '@udecode/plate-ui-toolbar';

const getMediaEmbedElementStyles = props => {
  const {
    selected,
    readOnly,
    provider
  } = props;
  let boxShadow;

  if (!readOnly && selected) {
    boxShadow = `0 0 0 1px rgb(59 130 249)`;
  }

  const providersPadding = {
    youtube: '56.2061%',
    vimeo: '75%',
    youku: '56.25%',
    dailymotion: '56.0417%',
    coub: '51.25%'
  };
  const providerPadding = provider !== 'twitter' ? provider && providersPadding[provider] || '56.0417%' : undefined;
  return createStyles({
    prefixClassNames: 'MediaEmbedElement',
    ...props
  }, {
    root: {
      "position": "relative",
      "marginTop": "0.25rem",
      "marginBottom": "0.25rem"
    },
    iframeInputWrapper: {
      "display": "flex",
      "flexDirection": "column",
      "alignItems": "center",
      "justifyContent": "center",
      "cursor": "text"
    },
    iframeWrapper: [{
      "position": "relative",
      "width": "100%"
    }, provider === 'twitter' ? css([".twitter-tweet{margin:0 auto !important;padding:2px;box-shadow:", ";}"], boxShadow) : css(["iframe{border-radius:3px;box-shadow:", ";}"], boxShadow), css(["padding-bottom:", ";"], providerPadding)],
    iframe: [{
      "position": "absolute",
      "top": "0px",
      "left": "0px",
      "width": "100%",
      "height": "100%"
    }],
    input: [{
      "width": "100%"
    }, css(["padding:0.5em;font-size:0.85em;border:2px solid #ddd;background:#fafafa;margin-top:5px;"])]
  });
};

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}

const twitterRegex = /^https?:\/\/twitter\.com\/(?:#!\/)?(\w+)\/status(es)?\/(?<id>\d+)/;
const parseTwitterUrl = url => {
  if (url.match(twitterRegex)) {
    var _twitterRegex$exec, _twitterRegex$exec$gr;

    return {
      provider: 'twitter',
      id: (_twitterRegex$exec = twitterRegex.exec(url)) === null || _twitterRegex$exec === void 0 ? void 0 : (_twitterRegex$exec$gr = _twitterRegex$exec.groups) === null || _twitterRegex$exec$gr === void 0 ? void 0 : _twitterRegex$exec$gr.id,
      url
    };
  }
};

const YOUTUBE_PREFIX = 'https://www.youtube.com/embed/';
const VIMEO_PREFIX = 'https://player.vimeo.com/video/';
const DAILYMOTION_PREFIX = 'https://www.dailymotion.com/embed/video/';
const YOUKU_PREFIX = 'https://player.youku.com/embed/';
const COUB_PREFIX = 'https://coub.com/embed/';
const parseVideoUrl = url => {
  const videoData = videoParser.parse(url);

  if (videoData !== null && videoData !== void 0 && videoData.provider && videoData.id) {
    const {
      id,
      provider
    } = videoData;
    const providerUrls = {
      youtube: `${YOUTUBE_PREFIX}${id}`,
      vimeo: `${VIMEO_PREFIX}${id}`,
      dailymotion: `${DAILYMOTION_PREFIX}${id}`,
      youku: `${YOUKU_PREFIX}${id}`,
      coub: `${COUB_PREFIX}${id}`
    };
    return {
      id,
      provider,
      url: providerUrls[provider]
    };
  }
};

const parseEmbedUrl = url => {
  const twitterData = parseTwitterUrl(url);
  if (twitterData) return twitterData;
  const videoData = parseVideoUrl(url);
  if (videoData) return videoData;
  return {};
};

const MediaEmbedUrlInput = ({
  url,
  onChangeValue,
  ...props
}) => {
  const [value, setValue] = React.useState(url);

  const validateUrl = newUrl => {
    // if not starting with http, assume pasting of full iframe embed code
    if (newUrl.substring(0, 4) !== 'http') {
      var _newUrl$match, _src$match;

      const regexMatchSrc = /src=".*?"/;
      const regexGroupQuotes = /"([^"]*)"/;
      const src = (_newUrl$match = newUrl.match(regexMatchSrc)) === null || _newUrl$match === void 0 ? void 0 : _newUrl$match[0];
      const returnString = src === null || src === void 0 ? void 0 : (_src$match = src.match(regexGroupQuotes)) === null || _src$match === void 0 ? void 0 : _src$match[1];

      if (returnString) {
        newUrl = returnString;
      }
    }

    return newUrl;
  };

  return /*#__PURE__*/React.createElement("input", _extends({
    style: {
      width: 276
    },
    value: value,
    placeholder: "Paste the embed link...",
    onClick: e => e.stopPropagation(),
    onChange: e => {
      const newUrl = e.target.value;
      validateUrl(newUrl);
      setValue(newUrl);
      onChangeValue === null || onChangeValue === void 0 ? void 0 : onChangeValue(newUrl);
    }
  }, props));
};

const WIDGET_SCRIPT_URL = 'https://platform.twitter.com/widgets.js';

const hasScriptInserted = () => document.querySelector(`script[src="${WIDGET_SCRIPT_URL}"]`);

const Tweet = ({
  tweetId,
  onError,
  onLoad,
  loadingComponent
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef(null);
  const previousTweetIDRef = useRef('');
  const createTweet = useCallback(async () => {
    try {
      // @ts-expect-error Twitter is attached to the window.
      await window.twttr.widgets.createTweet(tweetId, containerRef.current);
      setIsLoading(false);

      if (onLoad) {
        onLoad();
      }
    } catch (error) {
      if (onError) {
        onError(String(error));
      }
    }
  }, [onError, onLoad, tweetId]);
  useEffect(() => {
    if (tweetId !== previousTweetIDRef.current) {
      setIsLoading(true);

      if (!hasScriptInserted()) {
        var _document$body;

        const script = document.createElement('script');
        script.src = WIDGET_SCRIPT_URL;
        script.async = true;
        (_document$body = document.body) === null || _document$body === void 0 ? void 0 : _document$body.appendChild(script);
        script.onload = createTweet;
        script.onerror = onError || console.error;
      } else {
        createTweet();
      }

      if (previousTweetIDRef) {
        previousTweetIDRef.current = tweetId;
      }
    }
  }, [createTweet, onError, onLoad, tweetId]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, isLoading ? loadingComponent : null, /*#__PURE__*/React.createElement("div", {
    ref: containerRef
  }));
};

const MediaEmbedElement = ({
  tweetProps,
  inputProps,
  getIframeProps,
  disableInput,
  disableUnknownProviders,
  ...props
}) => {
  var _styles$iframeInputWr, _styles$iframeInputWr2, _styles$iframeWrapper, _styles$iframeWrapper2, _styles$iframe, _styles$iframe2, _styles$input, _styles$input2;

  const {
    attributes,
    children,
    nodeProps,
    element,
    editor
  } = props;
  const selected = useSelected();
  const readOnly = useReadOnly();
  const rootProps = getRootProps(props);
  const {
    url: elementUrl
  } = element;
  const embedUrlData = useMemo(() => parseEmbedUrl(elementUrl), [elementUrl]);
  const {
    provider,
    id,
    url
  } = embedUrlData;
  const styles = getMediaEmbedElementStyles({ ...props,
    provider,
    selected,
    readOnly
  });
  const iframeProps = useMemo(() => getIframeProps === null || getIframeProps === void 0 ? void 0 : getIframeProps({
    element,
    embedUrlData
  }), [element, embedUrlData, getIframeProps]);
  const isTwitter = provider === 'twitter' && id;
  return /*#__PURE__*/React.createElement(_StyledDiv, _extends({}, attributes, {
    className: styles.root.className
  }, rootProps, {
    $_css: styles.root.css
  }), /*#__PURE__*/React.createElement(_StyledDiv2, {
    className: (_styles$iframeInputWr = styles.iframeInputWrapper) === null || _styles$iframeInputWr === void 0 ? void 0 : _styles$iframeInputWr.className,
    contentEditable: false,
    draggable: true,
    $_css2: (_styles$iframeInputWr2 = styles.iframeInputWrapper) === null || _styles$iframeInputWr2 === void 0 ? void 0 : _styles$iframeInputWr2.css
  }, /*#__PURE__*/React.createElement(_StyledDiv3, {
    className: (_styles$iframeWrapper = styles.iframeWrapper) === null || _styles$iframeWrapper === void 0 ? void 0 : _styles$iframeWrapper.className,
    $_css3: (_styles$iframeWrapper2 = styles.iframeWrapper) === null || _styles$iframeWrapper2 === void 0 ? void 0 : _styles$iframeWrapper2.css
  }, isTwitter ? /*#__PURE__*/React.createElement(Tweet, _extends({
    tweetId: id,
    loadingComponent: "...loading"
  }, tweetProps)) : /*#__PURE__*/React.createElement(_StyledIframe, _extends({
    className: (_styles$iframe = styles.iframe) === null || _styles$iframe === void 0 ? void 0 : _styles$iframe.className,
    title: "embed",
    src: !url && disableUnknownProviders ? undefined : url,
    frameBorder: "0",
    allowFullScreen: true
  }, nodeProps, iframeProps, {
    $_css4: (_styles$iframe2 = styles.iframe) === null || _styles$iframe2 === void 0 ? void 0 : _styles$iframe2.css
  }))), !disableInput && !readOnly && selected && /*#__PURE__*/React.createElement(_StyledMediaEmbedUrlInput, _extends({
    "data-testid": "MediaEmbedUrlInput",
    className: (_styles$input = styles.input) === null || _styles$input === void 0 ? void 0 : _styles$input.className,
    url: elementUrl,
    onChangeValue: value => {
      const path = findNodePath(editor, element);
      if (!path) return;
      setNodes(editor, {
        url: value
      }, {
        at: path
      });
    }
  }, inputProps, {
    $_css5: (_styles$input2 = styles.input) === null || _styles$input2 === void 0 ? void 0 : _styles$input2.css
  }))), children);
};

var _StyledDiv = _styled("div").withConfig({
  displayName: "MediaEmbedElement___StyledDiv",
  componentId: "sc-1cvmtxu-0"
})(["", ""], p => p.$_css);

var _StyledDiv2 = _styled("div").withConfig({
  displayName: "MediaEmbedElement___StyledDiv2",
  componentId: "sc-1cvmtxu-1"
})(["", ""], p => p.$_css2);

var _StyledDiv3 = _styled("div").withConfig({
  displayName: "MediaEmbedElement___StyledDiv3",
  componentId: "sc-1cvmtxu-2"
})(["", ""], p => p.$_css3);

var _StyledIframe = _styled("iframe").withConfig({
  displayName: "MediaEmbedElement___StyledIframe",
  componentId: "sc-1cvmtxu-3"
})(["", ""], p => p.$_css4);

var _StyledMediaEmbedUrlInput = _styled(MediaEmbedUrlInput).withConfig({
  displayName: "MediaEmbedElement___StyledMediaEmbedUrlInput",
  componentId: "sc-1cvmtxu-4"
})(["", ""], p => p.$_css5);

const MediaEmbedToolbarButton = ({
  id,
  getEmbedUrl,
  ...props
}) => {
  const editor = usePlateEditorRef(id);
  return /*#__PURE__*/React.createElement(ToolbarButton, _extends({
    onMouseDown: async event => {
      if (!editor) return;
      event.preventDefault();
      let url;

      if (getEmbedUrl) {
        url = await getEmbedUrl();
      } else {
        url = window.prompt('Enter the URL of the embed:');
      }

      if (!url) return;
      insertMediaEmbed(editor, {
        url
      });
    }
  }, props));
};

export { MediaEmbedElement, MediaEmbedToolbarButton, MediaEmbedUrlInput, Tweet, getMediaEmbedElementStyles, parseEmbedUrl, parseTwitterUrl, parseVideoUrl };
//# sourceMappingURL=index.es.js.map
